import { CanonicalDataType } from "@/shared/domain/topics";

/** An attribute of a field the topic's partitions can disagree about. */
export type ConflictingAttribute = "canonical_data_type" | "data_type" | "unit";

/**
 * One field of a topic's schema, as the field catalogue describes it.
 *
 * ## This is an adapter, deliberately
 *
 * `GET /v1/urdf/bindable_topic_message_paths` carries each field's
 * `canonical_data_type`, `data_type` (the source spelling, e.g. `float64[7]`)
 * and `unit` alongside its path. It is a separate endpoint from
 * `/v1/topics/unique/topic_fields`, which serves paths alone and is left
 * untouched, so no client had its wire contract changed and no API-version
 * transform is involved.
 *
 * The catch is deployment: against an environment that predates this endpoint
 * the request 404s and the query yields nothing at all. So everything downstream
 * reads this type, and {@link adaptBindableMessagePaths} is the only place that knows what
 * the wire looks like. Every attribute but `path` is optional, and code reading
 * them has to behave sensibly when they are absent.
 */
export interface UrdfBindableMessagePath {
  /** Dot-joined source-schema path, e.g. `position` or `linear_acceleration.x`. */
  path: string;
  /** Roboto's normalized type, when every contributing schema version agrees. */
  canonicalDataType?: CanonicalDataType;
  /** The source schema's own spelling, e.g. `float64[7]` or `double`. */
  dataType?: string;
  /** The unit the source schema declares, e.g. `rad`, `deg`, `m`. */
  unit?: string;
  /**
   * The attributes the topic's **partitions disagree about**.
   *
   * A topic name is one identity over many partitions, which may sit on
   * different schema versions: `position` can be `float64[7]` in one recording
   * and `float64[]` in another. The catalogue populates an attribute only when
   * every version agrees, and names the rest here — so an absent value from
   * disagreement is never confused with one that was simply never set.
   *
   * Agreement is judged **per attribute**, which is what makes the array guard
   * survive it: `float64[7]` against `float64[]` conflicts on `data_type` while
   * both still agree the `canonical_data_type` is an array.
   */
  conflicting?: ConflictingAttribute[];
}

/** Whether the topic's partitions disagree about one attribute of a field. */
export function hasConflict(
  field: UrdfBindableMessagePath,
  attribute: ConflictingAttribute,
): boolean {
  return field.conflicting?.includes(attribute) ?? false;
}

/**
 * Whether a field holds several values, one, or whether we cannot tell.
 *
 * `"unknown"` is a first-class answer, not a failure: before the catalogue
 * carries types, no answer is available, and inventing one is exactly the
 * mistake this exists to prevent.
 */
export type FieldArity = "array" | "scalar" | "unknown";

/**
 * Whether a source-schema spelling names an array — `float64[7]`, `double[]`.
 *
 * Kept to the trailing-bracket form deliberately. It is what ROS, protobuf and
 * every schema spelling this catalogue carries use, and a cleverer parse would
 * be guessing about spellings nobody has seen.
 */
const ARRAY_SPELLING = /\[[^\]]*\]\s*$/;

/**
 * The canonical type when it actually says something, otherwise nothing.
 *
 * `CanonicalDataType.Unknown` is a value the catalogue really stores — anything
 * ingestion could not classify (a `null` column, a `date32`, a non-UTC
 * timestamp) lands there — and it means exactly what an absent type means: no
 * information. Reading it as a type in its own right would make the server
 * *admitting* it does not know strictly worse than it never having said
 * anything, which is backwards.
 */
function informativeCanonicalType(
  field: UrdfBindableMessagePath,
): CanonicalDataType | undefined {
  return field.canonicalDataType === CanonicalDataType.Unknown
    ? undefined
    : field.canonicalDataType;
}

/**
 * The source schema's own spelling, when it actually names a type.
 *
 * Not every `data_type` the catalogue serves is a spelling. Some topics come
 * back with `data_type` **restating `canonical_data_type` verbatim** — the
 * literal string `"array"` beside a canonical type of `array`, rather than an
 * element-typed spelling like `float64[7]`. Confirmed against the live
 * catalogue for `ik_controller:joint_state`, whose arm, hand, neck and base
 * fields are all reported that way.
 *
 * An echo carries no information the canonical type did not already carry, and
 * in particular says **nothing about a canonical array's elements**. Treating it
 * as a spelling is what made {@link isNumericField} read "not a number" out of a
 * field that never claimed to be one — an absence of evidence read as evidence
 * of absence. So an echo reads as no spelling at all, and callers fall through
 * to whatever they already do when the catalogue reports no type.
 *
 * Compared against the *raw* `canonicalDataType`, so a `data_type` of
 * `"unknown"` beside a canonical `unknown` is caught too, and trimmed and
 * case-folded, because casing and whitespace there are the schema producer's
 * rather than a difference in what was said.
 */
export function sourceDataType(
  field: UrdfBindableMessagePath,
): string | undefined {
  if (field.dataType === undefined) {
    return undefined;
  }
  const canonical: string | undefined = field.canonicalDataType;
  return field.dataType.trim().toLowerCase() === canonical
    ? undefined
    : field.dataType;
}

/**
 * What this field's arity is, from whatever the catalogue told us.
 *
 * The canonical type is preferred — it is Roboto's own normalization, and it is
 * what the message-path tree itself is built from — with the source spelling as
 * the fallback for a catalogue that carries one but not the other.
 */
export function fieldArity(field: UrdfBindableMessagePath): FieldArity {
  const canonical = informativeCanonicalType(field);
  if (canonical !== undefined) {
    return canonical === CanonicalDataType.Array ||
      canonical === CanonicalDataType.NumberArray
      ? "array"
      : "scalar";
  }
  // Only a spelling that says something the canonical type did not — an echo of
  // an uninformative canonical type is still nothing, and answering "scalar"
  // from nothing is the array trap.
  const spelling = sourceDataType(field);
  if (spelling !== undefined) {
    return ARRAY_SPELLING.test(spelling) ? "array" : "scalar";
  }
  return "unknown";
}

/**
 * Whether a source spelling names a number — `float64[7]`, `int32[]`, `double`.
 *
 * Anchored at the head so the element type decides, brackets or not, and so a
 * package-qualified struct (`geometry_msgs/Point[]`) and the other primitives
 * (`string[]`, `bool[]`, `time`) fall out. Only used to read the element type of
 * a canonical `array`, which is silent about what it holds.
 */
const NUMERIC_SPELLING = /^(?:float|double|single|u?int|number|real)/i;

/**
 * Whether a field could drive a joint: a number, or an array of them.
 *
 * A field of unknown type passes. Filtering it out would hide every field there
 * is from an environment whose catalogue does not carry types yet — the wrong
 * failure, since the cost of showing a non-numeric field is a binding that
 * plainly does not work, while the cost of hiding a numeric one is a user who
 * cannot bind at all.
 *
 * A canonical `array` is the case that makes the source spelling load-bearing:
 * it is what the catalogue reports for a `sensor_msgs/JointState` `position`
 * (`float64[7]`) *and* for the `name` (`string[7]`) beside it, so the canonical
 * type alone cannot separate the array a joint binds from the array that labels
 * its elements. The spelling can, and where there is no spelling the field is
 * kept — same reasoning as the untyped case above.
 *
 * "No spelling" includes a `data_type` that merely **echoes** the canonical
 * type: some topics report the literal string `"array"` there instead of an
 * element-typed one, which is not a claim about the elements at all. See
 * {@link sourceDataType} — reading an echo as a spelling hid every array field
 * of a real robot behind a predicate that had nothing to judge them on.
 */
export function isNumericField(field: UrdfBindableMessagePath): boolean {
  switch (informativeCanonicalType(field)) {
    case CanonicalDataType.Number:
    case CanonicalDataType.NumberArray:
      return true;
    case CanonicalDataType.Array: {
      const spelling = sourceDataType(field);
      return spelling === undefined || NUMERIC_SPELLING.test(spelling);
    }
    case undefined:
      return true;
    default:
      return false;
  }
}

/**
 * Multipliers into the extension's SI units — radians for a joint that turns,
 * metres for one that slides.
 *
 * Only exact, unambiguous spellings are listed. A unit this does not recognise
 * yields no scale at all rather than a guessed one: an unscaled binding is
 * wrong by a factor a user can see and correct, while a wrongly *scaled* one is
 * wrong by a factor nothing records.
 */
const UNIT_SCALES: Readonly<Record<string, number>> = {
  rad: 1,
  radian: 1,
  radians: 1,
  deg: Math.PI / 180,
  degree: Math.PI / 180,
  degrees: Math.PI / 180,
  m: 1,
  metre: 1,
  metres: 1,
  meter: 1,
  meters: 1,
  mm: 0.001,
  millimetre: 0.001,
  millimetres: 0.001,
  millimeter: 0.001,
  millimeters: 0.001,
  cm: 0.01,
};

/**
 * The `scale` a field's declared unit implies, or `undefined` for none.
 *
 * `undefined` is also returned for a unit that already *is* SI (`rad`, `m`
 * report 1 and are then omitted by the caller): the extension's default is 1, so
 * writing it says nothing the absence does not.
 *
 * A mismatch between a declared unit and a hand-entered scale must **warn, never
 * silently convert** — two sources of unit truth are exactly what the binding
 * shape avoids by carrying no unit string of its own.
 */
export function scaleForUnit(unit: string | undefined): number | undefined {
  if (unit === undefined) {
    return undefined;
  }
  const scale = UNIT_SCALES[unit.trim().toLowerCase()];
  return scale === undefined || scale === 1 ? undefined : scale;
}

/**
 * Reads the field catalogue's response, old shape or new.
 *
 * Tolerant on purpose — this is the seam across a deploy, and the frontend
 * cannot know which side of the rollout the API it is talking to is on:
 *
 * - `{path, canonical_data_type, data_type, unit, conflicting_attributes}` — the
 *   current shape. All five keys are always present; the three attributes are
 *   nullable. `camelCase` is accepted for the same keys, so neither spelling of
 *   the contract needs a change here.
 * - `"position"` — what an API predating the widening returns, and what a client
 *   pinned to `2026-07-05` or earlier still gets: a path and nothing else.
 * - Anything unrecognised is dropped rather than surfaced as an empty path, so a
 *   shape this does not understand degrades to "fewer suggestions" instead of to
 *   a menu of blanks.
 *
 * The catalogue already returns **one object per distinct path**, so the merge
 * below is defensive rather than load-bearing: a repeated path collapses to one
 * entry, and any attribute the two spell differently is dropped and named in
 * `conflicting` — the same thing the server does across partitions, and for the
 * same reason. Offering one path twice with no way to tell the two apart is
 * worse than saying the type is not agreed.
 */
export function adaptBindableMessagePaths(
  response: unknown,
): UrdfBindableMessagePath[] {
  if (!Array.isArray(response)) {
    return [];
  }

  const byPath = new Map<string, UrdfBindableMessagePath>();
  for (const entry of response) {
    const field = adaptField(entry);
    if (field === undefined) {
      continue;
    }
    const existing = byPath.get(field.path);
    if (existing === undefined) {
      byPath.set(field.path, field);
      continue;
    }
    byPath.set(field.path, mergeField(existing, field));
  }
  return [...byPath.values()];
}

function adaptField(entry: unknown): UrdfBindableMessagePath | undefined {
  if (typeof entry === "string") {
    return entry === "" ? undefined : { path: entry };
  }
  if (typeof entry !== "object" || entry === null) {
    return undefined;
  }

  const record = entry as Record<string, unknown>;
  const path = firstString(record, ["path", "field_path", "source_path"]);
  if (path === undefined) {
    return undefined;
  }
  const canonical = firstString(record, [
    "canonical_data_type",
    "canonicalDataType",
  ]);
  const dataType = firstString(record, ["data_type", "dataType"]);
  const unit = firstString(record, ["unit"]);
  const conflicting = readConflicting(
    record.conflicting_attributes ?? record.conflictingAttributes,
  );

  return {
    path,
    ...(isCanonicalDataType(canonical) ? { canonicalDataType: canonical } : {}),
    ...(dataType !== undefined ? { dataType } : {}),
    ...(unit !== undefined ? { unit } : {}),
    ...(conflicting.length > 0 ? { conflicting } : {}),
  };
}

const CONFLICTING_ATTRIBUTES: readonly ConflictingAttribute[] = [
  "canonical_data_type",
  "data_type",
  "unit",
];

function readConflicting(value: unknown): ConflictingAttribute[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return CONFLICTING_ATTRIBUTES.filter((attribute) =>
    value.includes(attribute),
  );
}

/**
 * Two descriptions of one path: agree, or say they do not.
 *
 * Per attribute, never wholesale — `float64[7]` against `float64[]` disagrees
 * about the source spelling while both still say the field is an array, and
 * that surviving distinction is what lets the picker keep refusing a bare array
 * bind under a conflict.
 */
function mergeField(
  a: UrdfBindableMessagePath,
  b: UrdfBindableMessagePath,
): UrdfBindableMessagePath {
  const conflicting = new Set<ConflictingAttribute>([
    ...(a.conflicting ?? []),
    ...(b.conflicting ?? []),
  ]);
  const canonicalDataType = agreedValue(
    a.canonicalDataType,
    b.canonicalDataType,
    conflicting,
    "canonical_data_type",
  );
  const dataType = agreedValue(
    a.dataType,
    b.dataType,
    conflicting,
    "data_type",
  );
  const unit = agreedValue(a.unit, b.unit, conflicting, "unit");

  return {
    path: a.path,
    ...(canonicalDataType !== undefined ? { canonicalDataType } : {}),
    ...(dataType !== undefined ? { dataType } : {}),
    ...(unit !== undefined ? { unit } : {}),
    ...(conflicting.size > 0
      ? {
          conflicting: CONFLICTING_ATTRIBUTES.filter((a) => conflicting.has(a)),
        }
      : {}),
  };
}

/**
 * The value both descriptions give, or nothing — recording the disagreement.
 *
 * An absent value is *no opinion* rather than a contradiction, matching what the
 * catalogue itself does: a unit added in a later schema version still surfaces
 * instead of being suppressed by the versions that predate it.
 */
function agreedValue<T>(
  a: T | undefined,
  b: T | undefined,
  conflicting: Set<ConflictingAttribute>,
  attribute: ConflictingAttribute,
): T | undefined {
  if (a === undefined || b === undefined || a === b) {
    const agreed = a ?? b;
    return conflicting.has(attribute) ? undefined : agreed;
  }
  conflicting.add(attribute);
  return undefined;
}

function firstString(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value !== "") {
      return value;
    }
  }
  return undefined;
}

function isCanonicalDataType(
  value: string | undefined,
): value is CanonicalDataType {
  return (
    value !== undefined &&
    (Object.values(CanonicalDataType) as string[]).includes(value)
  );
}
